/**
 * Cálculos derivados para la interfaz. Siempre a partir del motor.
 */
import {
  achievements,
  averageProgress,
  daysSinceLastRelapse,
  evaluateHabit,
  gaps,
  streaks,
  successRate,
  todayItem,
  totalCompletions,
  type EngineSettings,
  type LocalDate,
  type TodayItem,
  type UEResult,
} from '@habitos/engine';
import { toEngineSettings, toHabitDef, type EntryRec, type HabitRec, type MoveRec, type OverrideRec, type VacationRec } from './model';
import type { AppData } from './data';

export interface HabitIndex {
  entriesBy: Map<string, EntryRec[]>;
  overridesBy: Map<string, OverrideRec[]>;
  /** Traslados vigentes por hábito (FR-SCH-12). Viaja en el índice para no cambiar cada llamada a `evaluate`. */
  movesBy: Map<string, MoveRec[]>;
}

export function indexData(data: AppData): HabitIndex {
  const entriesBy = new Map<string, EntryRec[]>();
  for (const e of data.entries) {
    const list = entriesBy.get(e.habitId);
    if (list) list.push(e);
    else entriesBy.set(e.habitId, [e]);
  }
  const overridesBy = new Map<string, OverrideRec[]>();
  for (const o of data.overrides) {
    const list = overridesBy.get(o.habitId);
    if (list) list.push(o);
    else overridesBy.set(o.habitId, [o]);
  }
  const movesBy = new Map<string, MoveRec[]>();
  for (const m of data.moves ?? []) {
    const list = movesBy.get(m.habitId);
    if (list) list.push(m);
    else movesBy.set(m.habitId, [m]);
  }
  return { entriesBy, overridesBy, movesBy };
}

export function liveHabits(habits: HabitRec[]): HabitRec[] {
  return habits.filter((h) => !h.deletedAt).sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
}

export function evaluate(
  h: HabitRec,
  idx: HabitIndex,
  vacations: VacationRec[],
  settings: EngineSettings,
  today: LocalDate,
  range?: { from?: LocalDate; to?: LocalDate },
): UEResult[] {
  return evaluateHabit(
    {
      habit: toHabitDef(h),
      entries: idx.entriesBy.get(h.id) ?? [],
      overrides: idx.overridesBy.get(h.id) ?? [],
      moves: idx.movesBy.get(h.id) ?? [],
      vacations,
      settings,
      today,
    },
    range,
  );
}

export function todayFor(h: HabitRec, idx: HabitIndex, vacations: VacationRec[], settings: EngineSettings, today: LocalDate): TodayItem | null {
  if (h.deletedAt) return null;
  return todayItem({
    habit: toHabitDef(h),
    entries: idx.entriesBy.get(h.id) ?? [],
    overrides: idx.overridesBy.get(h.id) ?? [],
    moves: idx.movesBy.get(h.id) ?? [],
    vacations,
    settings,
    today,
  });
}

export interface HabitStats {
  results: UEResult[];
  streak: ReturnType<typeof streaks>;
  rate: ReturnType<typeof successRate>;
  rate30: ReturnType<typeof successRate>;
  gaps30: ReturnType<typeof gaps>;
  avgProgress: number | null;
  completions: number;
  relapseDays: number | null;
  achievements: ReturnType<typeof achievements>;
}

export function habitStats(h: HabitRec, data: AppData, idx: HabitIndex, today: LocalDate): HabitStats {
  const settings = toEngineSettings(data.settings!);
  const results = evaluate(h, idx, data.vacations, settings, today);
  const from30 = new Date(Date.parse(`${today}T00:00:00Z`) - 29 * 86_400_000).toISOString().slice(0, 10);
  return {
    results,
    streak: streaks(results),
    rate: successRate(results),
    rate30: successRate(results, { from: from30, to: today }),
    gaps30: gaps(results, { from: from30, to: today }),
    avgProgress: averageProgress(results),
    completions: totalCompletions(results),
    relapseDays: h.kind === 'quit' ? daysSinceLastRelapse(toHabitDef(h), results, today) : null,
    achievements: achievements(results),
  };
}
